import React, { useEffect, useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

export default function Sidebar({ systemStatus, cycleCount, lastUpdate, indicatorSignals }) {
  const location = useLocation();
  const path = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open on mobile.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Close on Escape key.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const closeDrawer = useCallback(() => setMobileOpen(false), []);
  const toggleDrawer = useCallback(() => setMobileOpen((o) => !o), []);

  const renderIndicatorBadge = () => {
    if (!indicatorSignals) return null;
    const { tf5m, tf3m } = indicatorSignals;
    return (
      <div className="sidebar-signal-group" title="Intraday buy setup signals">
        <span className={`sidebar-signal-chip ${tf5m?.state === 'on' ? 'on' : 'off'}`}>5m</span>
        <span className={`sidebar-signal-chip ${tf3m?.state === 'on' ? 'on' : 'off'}`}>3m</span>
      </div>
    );
  };

  // Title for current route — used by the mobile top bar.
  const pageTitle = (() => {
    if (path === '/') return 'Dashboard';
    if (path.startsWith('/indicators')) return 'Indicators';
    if (path.startsWith('/strategies')) return 'Strategies';
    if (path === '/quick-order' || path === '/manual-trade') return 'Quick Order';
    if (path.startsWith('/rolling-strangle')) return 'Rolling Strangle';
    if (path.startsWith('/positions')) return 'Positions';
    if (path.startsWith('/option-chain')) return 'Option Chain';
    if (path.startsWith('/premium-chart')) return 'Premium Chart';
    if (path.startsWith('/day-classifier')) return 'Day Type';
    if (path.startsWith('/signal-alerts')) return 'Signal Alerts';
    if (path.startsWith('/trade-history')) return 'Trade History';
    if (path.startsWith('/backtest')) return 'Backtest';
    if (path.startsWith('/settings')) return 'Settings';
    return 'OptionSelling';
  })();

  return (
    <>
      {/* Mobile top app bar — only visible on small screens */}
      <header className="mobile-topbar" role="banner">
        <button
          type="button"
          className="mobile-menu-btn"
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileOpen}
          aria-controls="primary-sidebar"
          onClick={toggleDrawer}
        >
          <span className={`hamburger ${mobileOpen ? 'open' : ''}`} aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
        <div className="mobile-topbar-title">
          <div className="mobile-topbar-logo">OS</div>
          <span className="mobile-topbar-text">{pageTitle}</span>
        </div>
        <div className="mobile-topbar-status">
          <span
            className={`status-dot ${systemStatus?.market_hours ? 'live' : 'closed'}`}
            title={systemStatus?.market_hours ? 'Market Live' : 'Market Closed'}
          />
        </div>
      </header>

      {/* Backdrop for drawer */}
      <div
        className={`sidebar-backdrop ${mobileOpen ? 'open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <nav
        id="primary-sidebar"
        className={`sidebar ${mobileOpen ? 'open' : ''}`}
        aria-label="Primary navigation"
      >
        <div className="sidebar-brand">
          <div className="sidebar-logo">OS</div>
          <h1>OptionSelling</h1>
          <button
            type="button"
            className="sidebar-close-btn"
            aria-label="Close menu"
            onClick={closeDrawer}
          >
            ×
          </button>
        </div>

      <div className="sidebar-nav">
        <div className="sidebar-section-label">Overview</div>
        <NavLink to="/" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} end>
          <span className="sidebar-icon">📊</span>
          <span>Dashboard</span>
        </NavLink>
        <NavLink to="/indicators" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">📈</span>
          <span>Indicators</span>
          {renderIndicatorBadge()}
        </NavLink>

        <div className="sidebar-section-label">Trading</div>
        <NavLink to="/strategies" className={({ isActive }) => {
          const isStrategyPage = path.startsWith('/strategies') || path === '/rolling-strangle';
          return `sidebar-link ${isActive || isStrategyPage ? 'active' : ''}`;
        }}>
          <span className="sidebar-icon">⚡</span>
          <span>Strategies</span>
        </NavLink>
        <NavLink to="/quick-order" className={({ isActive }) => {
          const onQuick = isActive || path === '/manual-trade';
          return `sidebar-link ${onQuick ? 'active' : ''}`;
        }}>
          <span className="sidebar-icon">🎯</span>
          <span>Quick Order</span>
        </NavLink>

        <div className="sidebar-section-label">Analysis</div>
        <NavLink to="/positions" className={({ isActive }) => {
          return `sidebar-link ${isActive || path === '/premium-chart' || path === '/option-chain' ? 'active' : ''}`;
        }}>
          <span className="sidebar-icon">📋</span>
          <span>Positions</span>
        </NavLink>
        <NavLink to="/day-classifier" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">🧭</span>
          <span>Day Type</span>
        </NavLink>
        <NavLink to="/signal-alerts" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">🎯</span>
          <span>Signal Alerts</span>
        </NavLink>

        <div className="sidebar-section-label">History</div>
        <NavLink to="/trade-history" className={({ isActive }) => {
          return `sidebar-link ${isActive || path === '/backtest' ? 'active' : ''}`;
        }}>
          <span className="sidebar-icon">📅</span>
          <span>History</span>
        </NavLink>

        <div className="sidebar-divider" />
        <NavLink to="/settings" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">⚙️</span>
          <span>Settings</span>
        </NavLink>
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-status">
          <span className={`status-dot ${systemStatus?.market_hours ? 'live' : 'closed'}`} />
          <span>{systemStatus?.market_hours ? 'Market Live' : 'Market Closed'}</span>
        </div>
        <div className="sidebar-meta">
          <span>Cycle #{cycleCount}</span>
          <span className="sidebar-update">{lastUpdate || '—'}</span>
        </div>
      </div>
    </nav>
    </>
  );
}
